import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowDown, ArrowUp, Check, Gamepad2, Loader2,
  PanelLeft, Plus, Save, Search, X,
} from 'lucide-react';
import client from '../lib/api';
import MediaThumb from '../components/MediaThumb';
import { notifySettingsUpdated, useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import { useAdminPermissions } from './lib/permissions';
import { AdminSkeleton } from './components/PageState';

// Yan paneli çizen kabuklar: Merit ve onun kopyası olan özel Betrun şeması
// (menü maddesinin `theme` listesiyle aynı kalmalıdır, bkz. lib/navigation.js).
const SIDE_PANEL_THEMES = ['merit', 'betrun'];

const CATEGORY_OPTIONS = [
  ['', 'Tüm kategoriler'],
  ['slot', 'Slot oyunları'],
  ['live-casino', 'Canlı casino'],
  ['crash', 'Hızlı oyunlar'],
];

const orderedSelection = (gameIds, items) => {
  const byId = new Map((items || []).map((game) => [game.id, game]));
  return (gameIds || []).map((gameId) => byId.get(gameId) || ({
    id: gameId,
    name: 'Katalogda bulunamayan oyun',
    provider: 'Bu kayıt artık mevcut değil',
    available: false,
    missing: true,
  }));
};

const MeritSidePanel = () => {
  const settings = useSettings();
  const { can } = useAdminPermissions();
  const { toast } = useToast();
  const canWrite = can('content.write');

  const [selected, setSelected] = useState([]);
  const [initialIds, setInitialIds] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [limit, setLimit] = useState(100);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedIds = useMemo(() => selected.map((game) => game.id), [selected]);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const dirty = JSON.stringify(selectedIds) !== JSON.stringify(initialIds);
  const hasUnavailable = selected.some((game) => game.available === false);

  useEffect(() => {
    if (!settings.settingsLoaded || !SIDE_PANEL_THEMES.includes(settings.site_theme)) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    client.get('/admin/merit-side-panel')
      .then(({ data }) => {
        if (!active) return;
        const gameIds = data.game_ids || [];
        setSelected(orderedSelection(gameIds, data.items));
        setInitialIds(gameIds);
        setLimit(data.limit || 100);
      })
      .catch((error) => {
        if (!active) return;
        toast({
          title: 'Yan panel yüklenemedi',
          description: error.response?.data?.detail || 'Ayarlar alınamadı',
        });
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [settings.settingsLoaded, settings.site_theme, toast]);

  useEffect(() => {
    if (!SIDE_PANEL_THEMES.includes(settings.site_theme)) return undefined;
    let active = true;
    const timer = window.setTimeout(() => {
      setSearching(true);
      client.get('/admin/merit-side-panel/games', {
        params: { q: query.trim() || undefined, category: category || undefined, limit: 60 },
      })
        .then(({ data }) => {
          if (!active) return;
          setCandidates(data.items || []);
          setTotal(data.total || 0);
        })
        .catch((error) => {
          if (!active) return;
          setCandidates([]);
          setTotal(0);
          toast({
            title: 'Oyunlar aranamadı',
            description: error.response?.data?.detail || 'Katalog alınamadı',
          });
        })
        .finally(() => { if (active) setSearching(false); });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [category, query, settings.site_theme, toast]);

  const addGame = (game) => {
    if (!canWrite || selectedIdSet.has(game.id)) return;
    if (selected.length >= limit) {
      toast({ title: 'Yan panel dolu', description: `En fazla ${limit} oyun eklenebilir.` });
      return;
    }
    setSelected((current) => [...current, { ...game, available: true }]);
  };

  const removeGame = (gameId) => {
    if (!canWrite) return;
    setSelected((current) => current.filter((game) => game.id !== gameId));
  };

  const moveGame = (index, direction) => {
    if (!canWrite) return;
    const target = index + direction;
    if (target < 0 || target >= selected.length) return;
    setSelected((current) => {
      const reordered = [...current];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      return reordered;
    });
  };

  const save = async () => {
    if (!canWrite || !dirty || hasUnavailable) return;
    setSaving(true);
    try {
      const { data } = await client.patch('/admin/merit-side-panel', {
        game_ids: selectedIds,
      });
      const gameIds = data.game_ids || [];
      setSelected(orderedSelection(gameIds, data.items));
      setInitialIds(gameIds);
      notifySettingsUpdated();
      toast({ title: 'Yan panel kaydedildi', description: `${gameIds.length} oyun yayında.` });
    } catch (error) {
      toast({
        title: 'Yan panel kaydedilemedi',
        description: error.response?.data?.detail || 'Değişiklikler uygulanamadı',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!settings.settingsLoaded || loading) {
    return <AdminSkeleton rows={8} label="Merit yan paneli yükleniyor" />;
  }

  if (!SIDE_PANEL_THEMES.includes(settings.site_theme)) {
    return (
      <div className="admin-card admin-state" role="status">
        <PanelLeft size={30} />
        <h2>Yan Panel yalnızca Merit temasında kullanılır</h2>
        <p>Genel Ayarlar’dan Merit temasını etkinleştirdiğinizde bu bölüm yönetim menüsünde görünür.</p>
      </div>
    );
  }

  return (
    <div className="admin-merit-side-page">
      <div className="admin-page-heading">
        <div className="admin-page-heading-icon"><PanelLeft size={22} /></div>
        <div>
          <h1>Yan Panel</h1>
          <p>Merit temasının sol menüsünde gösterilecek oyunları seçin ve sıralayın</p>
        </div>
        <div className="admin-page-heading-meta">{selected.length} oyun seçili</div>
      </div>

      {!canWrite && (
        <div className="admin-card p-4 text-sm text-amber-200">
          Bu listeyi görüntüleyebilirsiniz; değiştirmek için içerik yazma yetkisi gerekir.
        </div>
      )}

      {hasUnavailable && (
        <div className="admin-merit-warning" role="alert">
          <AlertTriangle size={17} />
          <span>Yayından kalkmış bir oyun listede duruyor. Kaydetmeden önce bu kaydı kaldırın.</span>
        </div>
      )}

      <div className="admin-merit-side-layout">
        <section className="admin-card admin-merit-selected" aria-labelledby="selected-side-games">
          <div className="admin-merit-panel-head">
            <div>
              <h2 id="selected-side-games">Yan Panel Oyunları</h2>
              <p>Buradaki sıra sitedeki yukarıdan aşağıya oyun sırasıdır.</p>
            </div>
            <button
              type="button"
              className="admin-btn-primary inline-flex items-center gap-2"
              onClick={save}
              disabled={!canWrite || !dirty || saving || hasUnavailable}
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Kaydet
            </button>
          </div>

          <div className="admin-merit-selected-list">
            {selected.map((game, index) => (
              <article
                key={game.id}
                className={`admin-merit-selected-row ${game.available === false ? 'is-unavailable' : ''}`}
              >
                <span className="admin-merit-position">{index + 1}</span>
                {game.image ? (
                  <MediaThumb src={game.image} />
                ) : (
                  <span className="admin-merit-game-placeholder"><Gamepad2 size={18} /></span>
                )}
                <span className="admin-merit-game-copy">
                  <strong title={game.name}>{game.name}</strong>
                  <small>{game.provider || 'Sağlayıcı yok'}</small>
                </span>
                <span className="admin-merit-row-actions">
                  <button type="button" onClick={() => moveGame(index, -1)} disabled={!canWrite || index === 0} aria-label={`${game.name} yukarı taşı`}><ArrowUp size={14} /></button>
                  <button type="button" onClick={() => moveGame(index, 1)} disabled={!canWrite || index === selected.length - 1} aria-label={`${game.name} aşağı taşı`}><ArrowDown size={14} /></button>
                  <button type="button" className="danger" onClick={() => removeGame(game.id)} disabled={!canWrite} aria-label={`${game.name} oyununu kaldır`}><X size={14} /></button>
                </span>
              </article>
            ))}
            {selected.length === 0 && (
              <div className="admin-merit-empty">
                <PanelLeft size={25} />
                <p>Yan panelde henüz oyun yok.</p>
                <span>Sağdaki katalogdan oyun arayıp ekleyebilirsiniz.</span>
              </div>
            )}
          </div>
        </section>

        <section className="admin-card admin-merit-catalog" aria-labelledby="side-game-catalog">
          <div className="admin-merit-panel-head">
            <div>
              <h2 id="side-game-catalog">Oyun Ekle</h2>
              <p>Ad veya sağlayıcıyla arayın, kategoriyle süzün.</p>
            </div>
            <span className="admin-merit-result-count">{total} sonuç</span>
          </div>

          <div className="admin-merit-filters">
            <label className="admin-merit-search">
              <Search size={16} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="admin-input"
                placeholder="Oyun veya sağlayıcı ara..."
              />
              {searching && <Loader2 size={15} className="animate-spin" />}
            </label>
            <select value={category} onChange={(event) => setCategory(event.target.value)} className="admin-input">
              {CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>

          <div className="admin-merit-game-grid">
            {candidates.map((game) => {
              const isSelected = selectedIdSet.has(game.id);
              return (
                <article key={game.id} className={`admin-merit-game-card ${isSelected ? 'is-selected' : ''}`}>
                  <div className="admin-merit-game-art">
                    {game.image ? (
                      <MediaThumb src={game.image} />
                    ) : (
                      <Gamepad2 size={24} />
                    )}
                  </div>
                  <div className="admin-merit-game-copy">
                    <strong title={game.name}>{game.name}</strong>
                    <small>{game.provider || 'Sağlayıcı yok'}</small>
                  </div>
                  <button
                    type="button"
                    onClick={() => addGame(game)}
                    disabled={!canWrite || isSelected || selected.length >= limit}
                    aria-label={`${game.name} oyununu yan panele ekle`}
                  >
                    {isSelected ? <><Check size={14} /> Eklendi</> : <><Plus size={14} /> Ekle</>}
                  </button>
                </article>
              );
            })}
            {!searching && candidates.length === 0 && (
              <div className="admin-merit-empty col-span-full">
                <Search size={25} />
                <p>Oyun bulunamadı.</p>
                <span>Aramayı veya kategori filtresini değiştirin.</span>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default MeritSidePanel;
